/**
 * ParticipationChart — participação de cada item no total, no visual do Dashboard Sphere.
 *
 * Pedido do dono (2026-10-03): a tela de Custos (`/usage`) com gráficos por participação
 * "igual à dashboard", e animação do ZERO até o valor quando o gráfico entra na tela.
 *
 * Três leituras do mesmo dado:
 *   - rosca com a fatia de cada item (participação);
 *   - barras verticais no estilo do "Gasto por mês" (a maior em destaque, com brilho);
 *   - legenda com valor e % (texto acessível — o gráfico é decorativo para leitor de tela).
 *
 * Animação: as barras crescem de 0 à altura, a rosca preenche e os números contam de 0 ao
 * valor real. Começa quando o gráfico entra na viewport e recomeça quando os dados mudam
 * (ex.: filtro por execução). `prefers-reduced-motion` ou o toggle "Pausar animações" →
 * tudo já aparece no valor final, sem movimento. Nenhum número é inventado: o que se anima
 * é o caminho até o valor que veio da API.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInViewport, useMotionPreference, useReducedMotion } from './motion';

/** Duração da entrada (ms). */
export const DURACAO_DA_ENTRADA = 1100;

/** Cores das fatias, na ordem: azul/ciano predominam, violeta e as de apoio só depois. */
export const CORES_DAS_FATIAS = [
  'var(--sp-accent)', 'var(--sp-cyan)', 'var(--sp-violet)', 'var(--sp-success)',
  'var(--sp-warning)', '#5B8CFF', '#1FB5C9', '#A08BFF', '#7BE3C2', '#FFCF7A',
];

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/** Participação (0..1) de cada valor no total; total ≤ 0 → tudo 0 (sem NaN). */
export function participacoes(valores) {
  const total = valores.reduce((a, v) => a + Math.max(0, Number(v) || 0), 0);
  return valores.map((v) => (total > 0 ? Math.max(0, Number(v) || 0) / total : 0));
}

/** Progresso 0→1 da entrada; `ativo` falso mantém 0, `semMovimento` vai direto a 1. */
export function useProgressoDeEntrada({ ativo, semMovimento, chave, duracao = DURACAO_DA_ENTRADA }) {
  const [p, setP] = useState(semMovimento ? 1 : 0);
  useEffect(() => {
    if (semMovimento) { setP(1); return undefined; }
    if (!ativo) return undefined;
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setP(1);
      return undefined;
    }
    let raf = 0;
    let inicio = null;
    setP(0);
    const passo = (agora) => {
      if (inicio === null) inicio = agora;
      const t = Math.min(1, (agora - inicio) / duracao);
      setP(easeOutCubic(t));
      if (t < 1) raf = window.requestAnimationFrame(passo);
    };
    raf = window.requestAnimationFrame(passo);
    return () => window.cancelAnimationFrame(raf);
  }, [ativo, semMovimento, chave, duracao]);
  return p;
}

function Rosca({ fatias, progresso, centro, rotuloCentro }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  let acumulado = 0;
  return (
    <div className="sp-part-donut">
      <svg viewBox="0 0 140 140" aria-hidden="true">
        <circle cx="70" cy="70" r={r} className="sp-part-donut-track" />
        {fatias.map((f, i) => {
          const tam = f.share * circ * progresso;
          const desloc = -acumulado * circ * progresso;
          acumulado += f.share;
          return (
            <circle key={f.key} cx="70" cy="70" r={r} className="sp-part-donut-slice"
              data-destaque={i === 0 ? 'true' : undefined}
              style={{ stroke: f.cor, strokeDasharray: `${tam} ${circ}`, strokeDashoffset: desloc }} />
          );
        })}
      </svg>
      <div className="sp-part-donut-center">
        <span className="sp-part-donut-value">{centro}</span>
        <small>{rotuloCentro}</small>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   titulo: string,
 *   itens: Array<{ key?: string, label: string, valor: number, detalhe?: string }>,
 *   formatar: (n: number) => string,
 *   rotuloTotal?: string,
 *   vazio?: string,
 *   testId?: string,
 * }} props
 */
export function ParticipationChart({ titulo, itens, formatar, rotuloTotal = 'Total', vazio = 'Sem dados ainda.',
  testId }) {
  const ref = useRef(null);
  const visivel = useInViewport(ref);
  const reduzido = useReducedMotion();
  const { enabled } = useMotionPreference();
  const semMovimento = reduzido || !enabled;

  const lista = useMemo(() => (itens || [])
    .map((it, i) => ({ ...it, key: it.key || `${it.label}-${i}`, valor: Math.max(0, Number(it.valor) || 0) }))
    .sort((a, b) => b.valor - a.valor), [itens]);
  const shares = participacoes(lista.map((it) => it.valor));
  const fatias = lista.map((it, i) => ({ ...it, share: shares[i], cor: CORES_DAS_FATIAS[i % CORES_DAS_FATIAS.length] }));
  const total = lista.reduce((a, it) => a + it.valor, 0);
  const maior = lista.length ? lista[0].valor : 0;
  // recomeça a entrada quando os dados mudam (ex.: filtro por execução)
  const chave = lista.map((it) => `${it.key}:${it.valor}`).join('|');

  // a entrada só começa quando o gráfico aparece; depois de animada, não volta a zero ao rolar
  const [jaEntrou, setJaEntrou] = useState(false);
  useEffect(() => { if (visivel) setJaEntrou(true); }, [visivel]);
  const progresso = useProgressoDeEntrada({ ativo: jaEntrou, semMovimento, chave });

  const pct = (share) => `${(share * 100 * progresso).toLocaleString('pt-BR', {
    minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  const pctFinal = (share) => `${(share * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

  return (
    <section ref={ref} className="sp-panel sp-part" aria-label={titulo} data-testid={testId}
      data-progresso={progresso >= 1 ? 'final' : 'animando'}>
      <div className="sp-panel-header">
        <h3 className="sp-part-title">{titulo}</h3>
        {lista.length > 0 && (
          <span className="sp-part-total">
            {rotuloTotal}: <strong>{formatar(total * progresso)}</strong>
          </span>
        )}
      </div>
      {lista.length === 0 ? (
        <p className="sp-part-empty" role="status">{vazio}</p>
      ) : (
        <div className="sp-part-body">
          <Rosca fatias={fatias} progresso={progresso}
            centro={pct(fatias[0].share)} rotuloCentro={fatias[0].label} />
          <ul className="sp-chart sp-part-bars" aria-hidden="true">
            {fatias.map((f, i) => {
              const alvo = maior > 0 ? Math.max(2, (f.valor / maior) * 100) : 0;
              return (
                <li key={f.key} className={i === 0 ? 'is-selected' : undefined} title={f.label}>
                  <span className="sp-part-bar-pct">{pct(f.share)}</span>
                  <span className="sp-bar sp-part-bar" style={{ '--bar-height': `${alvo * progresso}%` }} />
                  <small className="sp-part-bar-label">{f.label}</small>
                </li>
              );
            })}
          </ul>
          <ol className="sp-part-legend">
            {fatias.map((f) => (
              <li key={f.key} data-testid="participacao-item">
                <span className="sp-part-dot" style={{ background: f.cor }} aria-hidden="true" />
                <span className="sp-part-name" title={f.label}>{f.label}</span>
                <span className="sp-part-value">{formatar(f.valor * progresso)}</span>
                <span className="sp-part-share" aria-label={`participação ${pctFinal(f.share)}`}>{pct(f.share)}</span>
                {f.detalhe ? <span className="sp-part-detail">{f.detalhe}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

export default ParticipationChart;
